/**
 * §P.5 fase 2 · Las convocatorias de examen.
 *
 * ## El borrador es lo que separa «mirando fechas» de «esto va a ocurrir»
 *
 * Un examen nace en `draft` y mientras lo sea **no se puede inscribir a nadie**. Así
 * que la lista lo marca y la ficha lo explica: no es un estado administrativo, es la
 * diferencia entre una idea y una convocatoria.
 *
 * ## Kiap no aloja las fotos del examen
 *
 * `external_album_url` es un enlace al álbum donde el club ya las publica. Está en el
 * formulario con esa frase, porque un campo llamado «álbum» invita a pensar que las
 * subes aquí.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha, fechaYHora } from '@/shared/ui/formato';
import { aCentimos } from '@/club/cuotas/tipos';
import type { Disciplina } from '@/club/grupos/tipos';
import type { Sede } from '@/club/ajustes/tipos';
import {
  ESTADOS_DE_EXAMEN,
  pastillaDeExamen,
  type Examen,
} from '@/club/grados/tiposDeExamen';

export function Examenes({
  club,
  puedeEditar,
  alAbrirExamen,
}: {
  club: string;
  puedeEditar: boolean;
  alAbrirExamen: (examenId: string) => void;
}) {
  const cache = useQueryClient();
  const [convocando, setConvocando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [disciplina, setDisciplina] = useState('');
  const [cuando, setCuando] = useState('');
  const [sede, setSede] = useState('');
  const [limite, setLimite] = useState('');
  const [precio, setPrecio] = useState('');
  const [album, setAlbum] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const examenes = useQuery({
    queryKey: ['examenes', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['exam-events'].get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Examen[];
    },
  });

  // Las disciplinas del catálogo global: un examen es de una disciplina concreta.
  const disciplinas = useQuery({
    queryKey: ['disciplinas'],
    enabled: convocando,
    queryFn: async () => {
      const { data, error } = await api.v1.disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Disciplina[];
    },
  });

  const sedes = useQuery({
    queryKey: ['sedes', club],
    enabled: convocando,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .venues.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Sede[];
    },
  });

  const convocar = useMutation({
    mutationFn: async () => {
      const centimos = precio.trim() === '' ? null : aCentimos(precio);
      if (precio.trim() !== '' && centimos === null) {
        throw new ErrorDeApi({
          title: 'Ese precio no se entiende',
          status: 400,
          detail:
            'Escríbelo como 15 o 15,50. En blanco si el examen es gratis.',
        });
      }

      const r = await api.v1.clubs({ club })['exam-events'].post(
        {
          discipline_id: disciplina,
          name: nombre.trim(),
          // `datetime-local` da «2026-10-04T10:00»; el contrato pide `date-time`.
          scheduled_at: new Date(cuando).toISOString(),
          venue_id: sede === '' ? null : sede,
          registration_deadline: limite === '' ? null : limite,
          fee_cents: centimos,
          external_album_url: album.trim() === '' ? null : album.trim(),
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Examen;
    },
    onSuccess: (e) => {
      setConvocando(false);
      setNombre('');
      setCuando('');
      setPrecio('');
      setAlbum('');
      void cache.invalidateQueries({ queryKey: ['examenes', club] });
      // Directo a la ficha: lo siguiente es inscribir, y está allí.
      alAbrirExamen(e.id);
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (convocando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          convocar.mutate();
        }}
      >
        <h1>Convocar un examen</h1>

        <div className="rejilla">
          <label>
            Cómo se llama
            <input
              required
              maxLength={120}
              placeholder="Examen de diciembre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>

          <label>
            De qué disciplina
            <select
              required
              value={disciplina}
              onChange={(e) => setDisciplina(e.target.value)}
            >
              <option value="">Elige una</option>
              {(disciplinas.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Cuándo
            <input
              required
              type="datetime-local"
              value={cuando}
              onChange={(e) => setCuando(e.target.value)}
            />
          </label>

          <label>
            Dónde
            <select value={sede} onChange={(e) => setSede(e.target.value)}>
              <option value="">Sin decidir</option>
              {(sedes.data ?? [])
                .filter((s) => s.active)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Hasta cuándo se puede apuntar
            <input
              type="date"
              value={limite}
              onChange={(e) => setLimite(e.target.value)}
            />
          </label>

          <label>
            Qué cuesta
            <input
              inputMode="decimal"
              placeholder="En blanco si es gratis"
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
            />
          </label>
        </div>

        <label>
          Álbum de fotos
          <input
            type="url"
            maxLength={500}
            placeholder="https://..."
            value={album}
            onChange={(e) => setAlbum(e.target.value)}
          />
        </label>

        {/* Que quede claro qué es este campo: un enlace, no una subida. */}
        <p className="tenue">
          <strong>Kiap no guarda fotos del examen.</strong> Publícalas donde ya
          las publicas —Drive, Fotos, la web del club— y pega aquí el enlace
          para que las familias lo tengan a mano.
        </p>

        <div className="aviso" role="status">
          <strong>Nace como borrador.</strong> Mientras lo sea no se puede
          inscribir a nadie: es lo que separa «estoy mirando fechas» de «esto va
          a ocurrir». Se abre desde su ficha cuando la fecha esté decidida.
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={convocar.isPending}>
            {convocar.isPending ? 'Convocando…' : 'Convocar el examen'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={convocar.isPending}
            onClick={() => {
              setConvocando(false);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  if (examenes.isPending) {
    return <Cargando filas={4} etiqueta="Cargando los exámenes" />;
  }

  if (examenes.isError || !examenes.data) {
    return (
      <PanelError
        problema={aProblema(examenes.error)}
        reintentar={() => void examenes.refetch()}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Exámenes</h1>
        {puedeEditar && (
          <button type="button" onClick={() => setConvocando(true)}>
            Convocar un examen
          </button>
        )}
      </header>

      <p className="tenue">
        Las convocatorias del club, de la más próxima a la más antigua. De aquí
        salen los grados nuevos y <strong>los diplomas</strong>.
      </p>

      {examenes.data.length === 0 ? (
        <Vacio
          titulo="Todavía no hay exámenes"
          explicacion="Un examen convoca a los alumnos que cumplen los requisitos de su cinturón, se puntúa con una rúbrica y de él salen los grados y los diplomas."
          {...(puedeEditar
            ? {
                accion: (
                  <button type="button" onClick={() => setConvocando(true)}>
                    Convocar el primero
                  </button>
                ),
              }
            : {})}
        />
      ) : (
        <ul className="examenes">
          {examenes.data.map((e) => {
            const pinta = ESTADOS_DE_EXAMEN[e.status];

            return (
              <li
                key={e.id}
                className={
                  e.status === 'cancelled' || e.status === 'graded'
                    ? 'fila-fuera'
                    : undefined
                }
              >
                <div className="aviso-cabeza">
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => alAbrirExamen(e.id)}
                  >
                    <strong>{e.name}</strong>
                  </button>
                  <span className={`pastilla ${pastillaDeExamen(e.status)}`}>
                    {pinta?.etiqueta ?? e.status}
                  </span>
                  <span className="pastilla pastilla-left">
                    {e.discipline_name}
                  </span>
                </div>

                <p className="tenue">
                  {fechaYHora(e.scheduled_at)}
                  {e.venue_name ? ` · ${e.venue_name}` : ''}
                  {e.fee_cents !== null && e.fee_cents > 0
                    ? ` · ${euros(e.fee_cents)}`
                    : ''}
                </p>

                <div className="aviso-pie">
                  <span className="tenue">
                    {/*
                      Inscritos y calificados, porque son dos cifras distintas y la
                      diferencia es lo que queda por hacer.
                    */}
                    {e.registered_count === 0
                      ? 'Nadie inscrito'
                      : `${e.registered_count} ${e.registered_count === 1 ? 'inscrito' : 'inscritos'}`}
                    {e.graded_count > 0 ? ` · ${e.graded_count} con nota` : ''}
                    {e.registration_deadline
                      ? ` · se cierra el ${fecha(e.registration_deadline)}`
                      : ''}
                  </span>
                  <span className="tenue">{pinta?.explicacion}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
